import { useCallback, useEffect, useRef, useState } from 'react';
import { getMessages } from 'api';
import { getToken } from 'utils/auth';

// Base URL configuration. The value must be provided via the environment (same
// rule as api/client.js).
const BASE_URL = process.env.REACT_APP_API_URL;
if (!BASE_URL) {
  throw new Error('REACT_APP_API_URL is not set');
}

// The browser's native WebSocket API cannot send an Authorization header, so the
// JWT is passed as a query parameter instead (verified on the backend the same
// way as the REST endpoints).
const buildChatSocketUrl = (channelId) => {
  const token = getToken();
  const params = new URLSearchParams({ token: token || '', channel_id: channelId });
  return `${BASE_URL.replace(/^http/, 'ws')}/ws/chat?${params.toString()}`;
};

// Connects to a single chat channel: fetches its message history, opens a
// dedicated WebSocket connection scoped to that channel, and tracks the
// members currently present (via `presence` events pushed by the server).
//
// This is also the sole owner of the channel's WebSocket connection: secret
// chat (see useSecretChat) piggybacks on the same socket instead of opening a
// second one, by reading `secretMessages`/`sendSecretMessage` from this hook's
// return value rather than connecting on its own.
export const useChat = (channelId) => {
  const [messages, setMessages] = useState([]);
  const [members, setMembers] = useState([]);
  const [secretMessages, setSecretMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [connected, setConnected] = useState(false);
  const socketRef = useRef(null);

  useEffect(() => {
    if (!channelId) {
      setMessages([]);
      setLoading(false);
      return undefined;
    }

    let active = true;
    (async () => {
      try {
        setLoading(true);
        // Reset synchronously (before the WebSocket effect below opens its new
        // connection) so nothing from the previous channel can survive the merge.
        setMessages([]);
        const history = await getMessages(channelId);
        if (active) {
          // The WebSocket connection (effect below) runs concurrently and may already
          // have delivered messages for this channel before this history fetch
          // resolves. Merge by ID instead of replacing outright so none of those
          // are lost.
          setMessages((prev) => {
            const merged = [...history];
            const knownIds = new Set(history.map((m) => m.id));
            for (const message of prev) {
              if (!knownIds.has(message.id)) {
                merged.push(message);
              }
            }
            return merged;
          });
        }
      } catch (e) {
        if (active) setError(e.message);
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => {
      active = false;
    };
  }, [channelId]);

  useEffect(() => {
    setMembers([]);
    setSecretMessages([]);
    setConnected(false);

    if (!channelId) {
      return undefined;
    }

    const socket = new WebSocket(buildChatSocketUrl(channelId));
    socketRef.current = socket;

    socket.onopen = () => {
      setConnected(true);
      setError(null);
    };
    socket.onclose = () => setConnected(false);
    socket.onerror = () => setError('Failed to connect to the chat server');
    socket.onmessage = (event) => {
      let data;
      try {
        data = JSON.parse(event.data);
      } catch (e) {
        setError('Received a malformed message from the chat server');
        return;
      }
      switch (data.type) {
        case 'message': {
          const { type, ...message } = data;
          setMessages((prev) => [...prev, message]);
          break;
        }
        case 'secret_message': {
          const { type, ...message } = data;
          setSecretMessages((prev) => [...prev, message]);
          break;
        }
        case 'presence':
          setMembers(data.members);
          break;
        case 'channel_deleted':
          // The channel was deleted (e.g. from another tab or by another user).
          // The server closes the connection right after this message, so there
          // is nothing to reconnect to; just surface it to the user.
          setError('This channel has been deleted');
          break;
        case 'error':
          setError(data.message);
          break;
        default:
          break;
      }
    };

    return () => {
      // Detach the handlers before closing so a socket being discarded (e.g. React
      // Strict Mode's development-only double-invoke of this effect, or switching
      // to a different channel) cannot flip state after a new connection has
      // already taken over.
      socket.onopen = null;
      socket.onclose = null;
      socket.onerror = null;
      socket.onmessage = null;
      socket.close();
    };
  }, [channelId]);

  const send = useCallback((payload) => {
    const socket = socketRef.current;
    if (!socket || socket.readyState !== WebSocket.OPEN) {
      setError('Not connected to the chat server');
      return;
    }
    setError(null);
    socket.send(JSON.stringify(payload));
  }, []);

  const sendMessage = useCallback((body) => {
    const trimmed = body.trim();
    if (!trimmed) return;
    send({ body: trimmed });
  }, [send]);

  const sendSecretMessage = useCallback((body) => {
    const trimmed = body.trim();
    if (!trimmed) return;
    send({ type: 'secret', body: trimmed });
  }, [send]);

  return { messages, members, secretMessages, loading, error, connected, sendMessage, sendSecretMessage };
};
